/**
 * IPAMExplorer — /networks/explorer
 * Interactive: block → subnet → visual IP map → assign.
 * Reads optional ?blockId=X&subnetId=Y from URL to pre-select.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import IPAMLayout from './IPAMLayout';
import api from '../services/api';
import { SUBNET_TYPE_COLORS, subnetTypeLabel, useSubnetTypes } from '../utils/ipamTypes';
import { assignmentName, ciTypeLabel } from '../utils/ipamCi';

/* ── constants ── */
const ASSIGN_TYPE_COLORS = {
  static: 'secondary', carp: 'primary', vip: 'info',
  virtual: 'warning', vrrp: 'success', hsrp: 'danger',
};

function ipBadgeClass(status, isGateway, inDhcp) {
  if (isGateway)             return 'text-bg-info';
  if (inDhcp)                return '';          // custom style below
  if (status === 'assigned') return 'text-bg-danger';
  if (status === 'reserved') return 'text-bg-warning';
  if (status === 'conflict') return 'text-bg-dark';
  return 'text-bg-success';
}
function progressColor(pct) {
  if (pct >= 90) return 'bg-danger';
  if (pct >= 70) return 'bg-warning';
  return 'bg-success';
}
function lastOctet(ip) {
  const p = ip?.split('.');
  return p?.length === 4 ? `.${p[3]}` : ip;
}

function IpBadge({ entry, selectedIp, onSelect, compact, nameResolver }) {
  const inDhcp  = !!entry.in_dhcp_range;
  const clickOk = !inDhcp && (entry.status === 'free' || entry.status === 'reserved');
  const isSel   = selectedIp === entry.ip;
  const asigneeLines = (entry.assignments || []).map(a => {
    const typeLabel = ciTypeLabel(a.object_type);
    const name = assignmentName(a, nameResolver);
    const iface = a.interface_ref ? ` · ${a.interface_ref}` : '';
    return `${typeLabel}: ${name}${iface}`;
  });
  const tip = [
    entry.ip,
    entry.is_gateway ? '★ gateway' : null,
    inDhcp ? '📡 Rango DHCP — no disponible para asignación estática' : null,
    entry.dns_name ? `DNS: ${entry.dns_name}` : null,
    entry.comments  ? `Nota: ${entry.comments}` : null,
    ...asigneeLines,
  ].filter(Boolean).join('\n');

  const dhcpStyle = inDhcp ? {
    background: 'rgba(139,92,246,.16)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,.4)',
  } : {};

  return (
    <button
      type="button"
      className={`badge border-0 ${ipBadgeClass(entry.status, entry.is_gateway, inDhcp)}`}
      onClick={() => clickOk && onSelect(isSel ? '' : entry.ip)}
      title={tip}
      style={{
        cursor: clickOk ? 'pointer' : 'not-allowed',
        fontFamily: 'monospace',
        fontSize: compact ? '0.68em' : '0.78em',
        padding: compact ? '2px 4px' : '4px 7px',
        outline: isSel ? '2px solid #0d6efd' : 'none',
        outlineOffset: 1,
        boxShadow: isSel ? '0 0 0 3px rgba(13,110,253,.3)' : 'none',
        transition: 'box-shadow .12s',
        opacity: inDhcp ? 0.85 : 1,
        ...dhcpStyle,
      }}
    >
      {compact ? lastOctet(entry.ip) : entry.ip}
    </button>
  );
}

export default function IPAMExplorer({ companyId }) {
  // Carga el catálogo editable de tipos: los mapas de `ipamTypes` se actualizan
  // en el sitio, así que las etiquetas y colores salen ya con lo editado en
  // «Tipos» (la pantalla de subredes).
  useSubnetTypes(companyId);

  const [searchParams] = useSearchParams();
  const initBlockId  = searchParams.get('blockId')  || '';
  const initSubnetId = searchParams.get('subnetId') || '';

  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const errTimer = useRef(null);

  /* data */
  const [blocks, setBlocks]                 = useState([]);
  const [blockSubnetsData, setBlockSubnetsData] = useState(null);
  const [subnetMap, setSubnetMap]           = useState(null);
  const [assignments, setAssignments]       = useState([]);
  const [virtualGroups, setVirtualGroups]   = useState([]);
  const [ciCatalog, setCiCatalog]           = useState({ server: [], server_team: [], rack_element: [], workstation: [], printer: [], ip_phone: [] });

  /* selection */
  const [selectedBlockId, setSelectedBlockId]   = useState(initBlockId);
  const [selectedSubnetId, setSelectedSubnetId] = useState(initSubnetId);
  const [selectedIp, setSelectedIp]             = useState('');

  /* map config */
  const [mapLimit, setMapLimit]     = useState(256);
  const [compactMap, setCompactMap] = useState(true);
  const [loadingMap, setLoadingMap] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  /* manual IPv6 entry */
  const [manualIpInput, setManualIpInput] = useState('');

  /* assignment form */
  const [assignForm, setAssignForm]             = useState({ object_type: 'server', object_id: '', interface_ref: 'bmc', is_primary: true });
  const [assignmentType, setAssignmentType]     = useState('static');
  const [customInterfaceRef, setCustomInterfaceRef] = useState('');
  const [loadingCi, setLoadingCi]               = useState(false);
  const [interfaceOptions, setInterfaceOptions] = useState([]);
  const [loadingInterfaces, setLoadingInterfaces] = useState(false);

  useEffect(() => {
    if (error) {
      clearTimeout(errTimer.current);
      errTimer.current = setTimeout(() => setError(''), 6000);
    }
    return () => clearTimeout(errTimer.current);
  }, [error]);

  /* initial data load */
  const loadInitial = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setLoadingCi(true);
    try {
      const [b, a, vgRes] = await Promise.all([
        api.getIpamBlocks(companyId),
        api.getIpamAssignments(companyId),
        api.getIpamVirtualGroups(companyId),
      ]);
      const blockList = b.data || [];
      setBlocks(blockList);
      setAssignments(a.data || []);
      setVirtualGroups(vgRes?.data || []);

      if (!selectedBlockId && blockList.length > 0) {
        setSelectedBlockId(String(blockList[0].id));
      }

      const catalogResults = await Promise.allSettled([
        api.getServers(companyId),
        api.getElements(companyId),
        api.getWorkstations({ company_id: companyId }),
        api.getPrinters({ company_id: companyId }),
        api.getIPPhones(companyId),
      ]);
      const ex = r => r.status === 'fulfilled' ? (r.value?.data || []) : [];
      setCiCatalog({
        server:      ex(catalogResults[0]),
        server_team: ex(catalogResults[0]),
        rack_element:ex(catalogResults[1]),
        workstation: ex(catalogResults[2]),
        printer:     ex(catalogResults[3]),
        ip_phone:    ex(catalogResults[4]),
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); setLoadingCi(false); }
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadInitial(); }, [loadInitial]);

  /* load block's subnets whenever block changes */
  const loadBlockSubnets = useCallback(async (blockId) => {
    if (!blockId || !companyId) { setBlockSubnetsData(null); return; }
    try {
      const res = await api.getIpamBlockSubnets(Number(blockId), companyId);
      setBlockSubnetsData(res.data || null);
    } catch (e) { setError(e.message); }
  }, [companyId]);

  useEffect(() => { loadBlockSubnets(selectedBlockId); }, [selectedBlockId, loadBlockSubnets]);

  /* auto-select first subnet */
  useEffect(() => {
    const subs = blockSubnetsData?.subnets || [];
    if (subs.length > 0 && !selectedSubnetId) {
      setSelectedSubnetId(String(subs[0].id));
    }
  }, [blockSubnetsData]); // eslint-disable-line react-hooks/exhaustive-deps

  /* load subnet map */
  const loadSubnetMap = useCallback(async (subnetId) => {
    if (!subnetId || !companyId) { setSubnetMap(null); return; }
    setLoadingMap(true);
    try {
      const res = await api.getIpamSubnetMap(Number(subnetId), companyId, mapLimit);
      setSubnetMap(res.data || null);
    } catch (e) { setError(e.message); }
    finally { setLoadingMap(false); }
  }, [companyId, mapLimit]);

  useEffect(() => { loadSubnetMap(selectedSubnetId); }, [selectedSubnetId, loadSubnetMap]);

  /* Rack element types that carry a management IP */
  const MANAGED_RACK_TYPES = new Set([
    'switch', 'router', 'firewall', 'ap', 'wlc', 'ups', 'pdu',
    'kvm', 'console_server', 'load_balancer', 'nac', 'radius',
    'optical_switch', 'media_converter',
  ]);

  /* load interfaces when CI changes */
  const loadInterfaceOptions = useCallback(async (objectType, objectId, elementType) => {
    if (!objectType || !objectId) { setInterfaceOptions([]); return; }
    setLoadingInterfaces(true);
    try {
      const idNum = Number(objectId);
      let opts = [];
      if (objectType === 'server') {
        const comp = await api.getComponents(idNum, companyId);
        const allComps = comp?.data || [];
        const bmcComp = allComps.find(c => c.component_type === 'bmc');
        const nics = allComps.filter(c => c.component_type === 'nic')
          .flatMap(c => (c.ports || []).map(p => ({
            value: `nic-${p.id}`,
            label: `${c.model || 'NIC'} · ${p.label || `P${p.port_number}`}`,
          })));
        const bmcEntry = bmcComp && (bmcComp.ports || []).length > 0
          ? { value: `nic-${bmcComp.ports[0].id}`, label: `🔧 BMC / iLO / IPMI · ${bmcComp.ports[0].label || `P${bmcComp.ports[0].port_number}`}` }
          : { value: 'bmc', label: '🔧 BMC / iLO / IPMI' };
        opts = [
          bmcEntry,
          { value: 'mgmt', label: '🖥️ mgmt (red de gestión)' },
          ...nics,
        ];
      } else if (objectType === 'server_team') {
        const st = await api.getServerStorage(idNum, companyId);
        const teams = Array.isArray(st?.data?.data?.network_teams) ? st.data.data.network_teams : [];
        opts = teams.map((t, i) => {
          const name = (t?.name || '').trim() || `team-${i + 1}`;
          return { value: `team:${name}`, label: `${name}${t?.mode ? ` · ${t.mode}` : ''}` };
        });
      } else if (objectType === 'rack_element') {
        const pr = await api.getPorts(idNum, companyId);
        const portOpts = (pr?.data || []).map(p => ({
          value: `port-${p.id}`,
          label: `Puerto ${p.port_number}${p.label ? ` · ${p.label}` : ''}`,
        }));
        const et = (elementType || '').toLowerCase();
        if (MANAGED_RACK_TYPES.has(et)) {
          opts = [
            { value: 'mgmt', label: '🔧 Management (OOB / MGMT)' },
            ...portOpts,
          ];
        } else {
          opts = portOpts;
        }
      }
      setInterfaceOptions(opts);
      setAssignForm(prev => ({ ...prev, interface_ref: opts[0]?.value || 'bmc' }));
    } catch { setInterfaceOptions([]); }
    finally { setLoadingInterfaces(false); }
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* derived */
  const ciOptions = useMemo(() => {
    if (assignForm.object_type === 'virtual_group') {
      return virtualGroups.map(g => ({
        value: String(g.id),
        label: `${g.name} · ${(g.protocol || 'vip').toUpperCase()}${g.ip ? ` · ${g.ip}` : ' · sin VIP'}`,
      }));
    }
    const list = ciCatalog[assignForm.object_type] || [];
    return list.map(item => {
      const name = item.name || item.hostname || item.code || `#${item.id}`;
      const prefix = item.element_type ? `[${item.element_type}] ` : '';
      return { value: String(item.id), label: `${prefix}${name}` };
    });
  }, [ciCatalog, assignForm.object_type, virtualGroups]);

  const objectNameByRef = useMemo(() => {
    const map = new Map();
    Object.entries(ciCatalog).forEach(([type, rows]) => {
      rows.forEach(r => {
        const key = `${type}:${r.id}`;
        let n = r.name || r.code || r.hostname || `#${r.id}`;
        if (type === 'rack_element') n = `[${r.element_type}] ${n}`;
        map.set(key, n);
      });
    });
    return map;
  }, [ciCatalog]);

  const selectedSubnetSummary = subnetMap?.summary || null;
  const visualIps = subnetMap?.generated_ips || [];
  const isIpv6Map = !!(subnetMap?.is_ipv6);

  /* group IPs by /24 block for subnets wider than /24 — IPv4 only */
  const slash24Groups = useMemo(() => {
    if (isIpv6Map || !visualIps.length) return [];
    const groupMap = new Map();
    for (const entry of visualIps) {
      const prefix = entry.ip.split('.').slice(0, 3).join('.');
      if (!groupMap.has(prefix)) groupMap.set(prefix, []);
      groupMap.get(prefix).push(entry);
    }
    return Array.from(groupMap.entries()).map(([prefix, ips]) => ({
      prefix,
      label: `${prefix}.0/24`,
      ips,
      assignedCount: ips.filter(e => e.status === 'assigned').length,
      reservedCount: ips.filter(e => e.status === 'reserved').length,
      freeCount:     ips.filter(e => e.status === 'free').length,
    }));
  }, [visualIps]);

  const toggleGroup = useCallback((prefix) => setExpandedGroups(prev => {
    const next = new Set(prev);
    if (next.has(prefix)) next.delete(prefix); else next.add(prefix);
    return next;
  }), []);

  /* auto-expand groups with assignments; if all free, expand first */
  useEffect(() => {
    if (slash24Groups.length <= 1) return;
    const toExpand = new Set(
      slash24Groups
        .filter(g => g.assignedCount > 0 || g.reservedCount > 0)
        .map(g => g.prefix)
    );
    if (toExpand.size === 0) toExpand.add(slash24Groups[0].prefix);
    setExpandedGroups(toExpand);
  }, [slash24Groups]);

  /* actions */
  const assign = async (e) => {
    e.preventDefault();
    if (!selectedIp) { setError('Selecciona una IP libre en el mapa'); return; }
    /* ── virtual group: set/update the group's VIP IP ── */
    if (assignForm.object_type === 'virtual_group') {
      const group = virtualGroups.find(g => String(g.id) === String(assignForm.object_id));
      if (!group) { setError('Grupo VIP no encontrado'); return; }
      try {
        await api.updateIpamVirtualGroup(group.id, {
          name: group.name, protocol: group.protocol,
          ip: selectedIp,
          description: group.description || '',
          status: group.status || 'active',
        }, companyId);
        const [vgUpdated] = await Promise.all([
          api.getIpamVirtualGroups(companyId),
          loadSubnetMap(selectedSubnetId),
        ]);
        setVirtualGroups(vgUpdated.data || []);
        setSelectedIp('');
        setAssignForm(prev => ({ ...prev, object_id: '' }));
      } catch (err) { setError(err.message); }
      return;
    }
    try {
      await api.createIpamAssignment({
        ip: selectedIp,
        object_type: assignForm.object_type,
        object_id: Number(assignForm.object_id),
        interface_ref: customInterfaceRef || assignForm.interface_ref,
        is_primary: assignForm.object_type === 'server_team' || assignmentType !== 'static' ? false : !!assignForm.is_primary,
        assignment_type: assignmentType,
      }, companyId);
      setAssignForm(prev => ({ ...prev, object_id: '' }));
      setCustomInterfaceRef('');
      setInterfaceOptions([]);
      setSelectedIp('');
      const [aRes] = await Promise.all([
        api.getIpamAssignments(companyId),
        loadSubnetMap(selectedSubnetId),
      ]);
      setAssignments(aRes.data || []);
    } catch (e) { setError(e.message); }
  };

  const release = async (id) => {
    try {
      await api.deleteIpamAssignment(id, companyId);
      const [aRes] = await Promise.all([
        api.getIpamAssignments(companyId),
        loadSubnetMap(selectedSubnetId),
      ]);
      setAssignments(aRes.data || []);
    } catch (e) { setError(e.message); }
  };

  const currentSubnets = blockSubnetsData?.subnets || [];

  // Format block address count for display in selector
  const formatBlockAddrs = (b) => {
    const pct = b.occupation_pct || 0;
    if (b.version === 6) {
      const pfx = b.cidr ? parseInt(b.cidr.split('/')[1], 10) : null;
      return pfx != null ? `v6 / 2^${128 - pfx}` : 'v6';
    }
    return `${pct}%`;
  };

  return (
    <IPAMLayout>
      {error && (
        <div className="alert alert-danger py-2 d-flex justify-content-between">
          <span>{error}</span>
          <button className="btn-close" onClick={() => setError('')} />
        </div>
      )}

      <div className="row g-3">
        {/* ── LEFT: Block + subnet selectors ── */}
        <div className="col-md-3">
          {/* Block selector */}
          <div className="card mb-3">
            <div className="card-header py-2 d-flex justify-content-between align-items-center">
              <strong style={{ fontSize: '0.9em' }}>Bloque</strong>
              <span className="badge text-bg-secondary">{blocks.length}</span>
            </div>
            <div className="card-body p-2">
              <select
                className="form-select form-select-sm"
                value={selectedBlockId}
                onChange={e => { setSelectedBlockId(e.target.value); setSelectedSubnetId(''); setSelectedIp(''); }}
              >
                <option value="">Selecciona bloque…</option>
                {blocks.map(b => (
                  <option key={b.id} value={b.id}>{b.cidr} ({formatBlockAddrs(b)})</option>
                ))}
              </select>
              {selectedBlockId && blockSubnetsData?.summary && (() => {
                const selBlock = blocks.find(bb => String(bb.id) === String(selectedBlockId));
                const isV6Block = selBlock?.version === 6;
                const summ = blockSubnetsData.summary;
                return (
                  <div className="mt-2">
                    {isV6Block ? (
                      <small className="text-muted" style={{ fontSize: '0.71em' }}>
                        {summ.subnets_count || 0} subredes IPv6 registradas
                      </small>
                    ) : (
                      <>
                        <div className="progress" style={{ height: 6 }}>
                          <div
                            className={`progress-bar ${progressColor(summ.occupation_pct)}`}
                            style={{ width: `${Math.min(100, summ.occupation_pct)}%` }}
                          />
                        </div>
                        <small className="text-muted" style={{ fontSize: '0.71em' }}>
                          {summ.occupation_pct}% · {(summ.block_addresses || 0).toLocaleString()} IPs
                        </small>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Subnet list */}
          <div className="card">
            <div className="card-header py-2 d-flex justify-content-between align-items-center">
              <strong style={{ fontSize: '0.9em' }}>Subredes</strong>
              <span className="badge text-bg-secondary">{currentSubnets.length}</span>
            </div>
            <div className="card-body p-0" style={{ maxHeight: 340, overflow: 'auto' }}>
              {loading && <p className="text-muted small text-center py-2">Cargando…</p>}
              {!loading && currentSubnets.length === 0 && <p className="text-muted small text-center py-2">Sin subredes.</p>}
              <ul className="list-group list-group-flush">
                {currentSubnets.map(s => {
                  const isActive = String(s.id) === String(selectedSubnetId);
                  const tcol = SUBNET_TYPE_COLORS[s.type] || 'secondary';
                  return (
                    <li
                      key={s.id}
                      className={`list-group-item px-2 py-2 list-group-item-action ${isActive ? 'active' : ''}`}
                      role="button"
                      onClick={() => { setSelectedSubnetId(String(s.id)); setSelectedIp(''); }}
                      style={{ transition: 'background .1s' }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.88em' }}>{s.cidr}</span>
                        <div className="d-flex gap-1">
                          <span className={`badge text-bg-${tcol}`} style={{ fontSize: '0.65em' }}>{subnetTypeLabel(s.type || 'lan')}</span>
                          {s.vlan_number != null && <span className="badge text-bg-info" style={{ fontSize: '0.65em' }}>V{s.vlan_number}</span>}
                        </div>
                      </div>
                      {s.site && <small className="d-block" style={{ opacity: 0.7, fontSize: '0.75em' }}>{s.site}</small>}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        {/* ── MIDDLE: IP map ── */}
        <div className="col-md-5">
          <div className="card h-100">
            <div className="card-header py-2 d-flex justify-content-between align-items-center flex-wrap gap-1">
              <strong style={{ fontSize: '0.9em' }}>Mapa visual</strong>
              <div className="d-flex gap-2 align-items-center">
                <div className="form-check form-switch mb-0">
                  <input className="form-check-input" type="checkbox" id="compact" checked={compactMap} onChange={e => setCompactMap(e.target.checked)} />
                  <label className="form-check-label small" htmlFor="compact">Compacto</label>
                </div>
                <select className="form-select form-select-sm" style={{ width: 80 }} value={mapLimit} onChange={e => setMapLimit(Number(e.target.value))}>
                  <option value={128}>128</option>
                  <option value={256}>256</option>
                  <option value={512}>512</option>
                  <option value={1024}>1024</option>
                </select>
              </div>
            </div>
            <div className="card-body p-2" style={{ minHeight: 240 }}>
              {/* Legend */}
              <div className="mb-2 d-flex flex-wrap gap-1" style={{ fontSize: '0.72em' }}>
                <span className="badge text-bg-success">Libre</span>
                <span className="badge text-bg-danger">Asignada</span>
                <span className="badge text-bg-warning">Reservada</span>
                <span className="badge text-bg-info">Gateway</span>
                <span className="badge text-bg-dark">Conflicto</span>
                <span className="badge" style={{ background: 'rgba(139,92,246,.16)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,.4)' }}>DHCP</span>
              </div>

              {/* Summary badges */}
              {selectedSubnetSummary && (
                <div className="mb-2 d-flex flex-wrap gap-1" style={{ fontSize: '0.77em' }}>
                  <span className="badge text-bg-success">{selectedSubnetSummary.free > 1e13 ? '~todo libre' : `${selectedSubnetSummary.free} libres`}</span>
                  <span className="badge text-bg-danger">{selectedSubnetSummary.assigned} asig.</span>
                  <span className="badge text-bg-warning">{selectedSubnetSummary.reserved} res.</span>
                  {selectedSubnetSummary.conflict > 0 && <span className="badge text-bg-dark">{selectedSubnetSummary.conflict} conflicto</span>}
                  {selectedSubnetSummary.gateway && <span className="badge text-bg-info">GW {selectedSubnetSummary.gateway}</span>}
                  <span className="badge text-bg-light text-dark border">
                    {isIpv6Map
                      ? (selectedSubnetSummary.usable_ips_human || selectedSubnetSummary.usable_ips_exact || 'IPv6')
                      : `${selectedSubnetSummary.usable_ips} usables`}
                  </span>
                  {isIpv6Map && (
                    <span className="badge text-bg-info" style={{ fontSize: '0.72em' }}>IPv6</span>
                  )}
                </div>
              )}

              {subnetMap?.truncated && (
                <div className="alert alert-warning py-1 mb-2" style={{ fontSize: '0.77em' }}>
                  Parcial: {subnetMap.generated_count} IPs. Sube el límite para ver más.
                </div>
              )}

              {/* IPv6 notice */}
              {isIpv6Map && (
                <div className="alert alert-info py-2 mb-2 d-flex align-items-start gap-2" style={{ fontSize: '0.79em' }}>
                  <span style={{ fontSize: '1.1em' }}>i</span>
                  <div>
                    <strong>Subred IPv6</strong> — debido al tamaño del espacio de dirección se muestran solo las IPs registradas en la base de datos.
                    Para asignar una IP nueva, intróducela manualmente en el formulario de la derecha.
                    {selectedSubnetSummary?.registered_count === 0 && <> Sin IPs registradas aún.</>}
                  </div>
                </div>
              )}

              {loadingMap && (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm text-secondary" />
                  <p className="small text-muted mt-1">Cargando mapa…</p>
                </div>
              )}

              {!loadingMap && visualIps.length === 0 && !selectedSubnetId && (
                <p className="text-muted text-center small py-4">Selecciona una subred en la lista.</p>
              )}
              {!loadingMap && visualIps.length === 0 && selectedSubnetId && !isIpv6Map && (
                <p className="text-muted text-center small py-4">Sin IPs en esta subred.</p>
              )}
              {!loadingMap && visualIps.length === 0 && isIpv6Map && selectedSubnetId && (
                <p className="text-muted text-center small py-4">Sin IPs registradas en esta subred IPv6. Usa el formulario para asignar la primera.</p>
              )}

              {/* IP grid — grouped by /24 for subnets wider than /24; for IPv6 show only registered IPs */}
              {!loadingMap && (
                isIpv6Map ? (
                  // IPv6: show only registered IPs as badges
                  <div className="d-flex flex-wrap" style={{ gap: compactMap ? 3 : 5 }}>
                    {visualIps.map(entry => (
                      <IpBadge key={entry.ip} entry={entry} selectedIp={selectedIp} onSelect={setSelectedIp} compact={false} nameResolver={objectNameByRef} />
                    ))}
                  </div>
                ) : slash24Groups.length > 1 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {slash24Groups.map(group => {
                      const isOpen = expandedGroups.has(group.prefix);
                      return (
                        <div key={group.prefix} className="border rounded" style={{ overflow: 'hidden' }}>
                          <div
                            role="button"
                            className="d-flex align-items-center gap-2"
                            onClick={() => toggleGroup(group.prefix)}
                            style={{
                              padding: '4px 8px',
                              background: isOpen ? '#e8f0fe' : '#f8f9fa',
                              cursor: 'pointer',
                              borderBottom: isOpen ? '1px solid #dee2e6' : 'none',
                              userSelect: 'none',
                            }}
                          >
                            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82em' }}>
                              {group.label}
                            </span>
                            {group.assignedCount > 0 && (
                              <span className="badge text-bg-danger" style={{ fontSize: '0.63em' }} title="Asignadas">{group.assignedCount}</span>
                            )}
                            {group.reservedCount > 0 && (
                              <span className="badge text-bg-warning" style={{ fontSize: '0.63em' }} title="Reservadas">{group.reservedCount}</span>
                            )}
                            <span className="badge text-bg-success" style={{ fontSize: '0.63em' }} title="Libres">{group.freeCount}</span>
                            <span className="ms-auto text-muted" style={{ fontSize: '0.75em' }}>{isOpen ? '▲' : '▼'}</span>
                          </div>
                          {isOpen && (
                            <div className="d-flex flex-wrap p-1" style={{ gap: compactMap ? 3 : 5 }}>
                              {group.ips.map(entry => (
                                <IpBadge key={entry.ip} entry={entry} selectedIp={selectedIp} onSelect={setSelectedIp} compact={compactMap} nameResolver={objectNameByRef} />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="d-flex flex-wrap" style={{ gap: compactMap ? 3 : 5 }}>
                    {visualIps.map(entry => (
                      <IpBadge key={entry.ip} entry={entry} selectedIp={selectedIp} onSelect={setSelectedIp} compact={compactMap} nameResolver={objectNameByRef} />
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT: Assignment form ── */}
        <div className="col-md-4">
          <div className="card">
            <div className="card-header py-2 d-flex justify-content-between align-items-center">
              <strong style={{ fontSize: '0.9em' }}>Asignar IP</strong>
              {selectedIp
                ? <span className="badge text-bg-success" style={{ fontFamily: 'monospace', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedIp}</span>
                : <span className="badge text-bg-light text-muted border" style={{ fontSize: '0.75em' }}>Selecciona en el mapa</span>
              }
            </div>
            <div className="card-body p-2">
              {/* IPv6: manual IP entry */}
              {isIpv6Map && (
                <div className="mb-2">
                  <label className="form-label form-label-sm mb-1" style={{ fontSize: '0.8em', color: '#0284c7' }}>IP IPv6 para asignar</label>
                  <div className="input-group input-group-sm">
                    <input
                      className="form-control form-control-sm font-monospace"
                      placeholder="2001:db8::1"
                      value={manualIpInput}
                      onChange={e => setManualIpInput(e.target.value)}
                      onBlur={e => { if (e.target.value.trim()) setSelectedIp(e.target.value.trim()); }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (manualIpInput.trim()) setSelectedIp(manualIpInput.trim()); } }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => { if (manualIpInput.trim()) setSelectedIp(manualIpInput.trim()); }}
                    >✓</button>
                  </div>
                  {selectedIp && isIpv6Map && (
                    <div className="badge text-bg-success mt-1" style={{ fontFamily: 'monospace', fontSize: '0.72em', wordBreak: 'break-all' }}>{selectedIp}</div>
                  )}
                </div>
              )}
              <form onSubmit={assign} className="d-grid gap-2">
                <select className="form-select form-select-sm" value={assignForm.object_type}
                  onChange={e => { setAssignForm({ ...assignForm, object_type: e.target.value, object_id: '' }); setInterfaceOptions([]); }}>
                  <option value="server">Servidor</option>
                  <option value="server_team">Team de red</option>
                  <option value="rack_element">Elem. rack</option>
                  <option value="workstation">Workstation</option>
                  <option value="printer">Impresora</option>
                  <option value="ip_phone">Teléfono IP</option>
                  <option value="virtual_group">Grupo VIP / CARP</option>
                </select>

                <select className="form-select form-select-sm" value={assignForm.object_id}
                  onChange={async e => {
                    const v = e.target.value;
                    setAssignForm(prev => ({ ...prev, object_id: v }));
                    const et = assignForm.object_type === 'rack_element'
                      ? (ciCatalog.rack_element.find(r => String(r.id) === v)?.element_type || '')
                      : '';
                    await loadInterfaceOptions(assignForm.object_type, v, et);
                  }}
                  required disabled={loadingCi}
                >
                  <option value="">{loadingCi ? 'Cargando…' : 'CI / elemento…'}</option>
                  {ciOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>

                {assignForm.object_type !== 'virtual_group' && (
                  <>
                    <select className="form-select form-select-sm" value={assignForm.interface_ref}
                      onChange={e => setAssignForm({ ...assignForm, interface_ref: e.target.value })}
                      disabled={loadingInterfaces || interfaceOptions.length === 0}
                    >
                      {interfaceOptions.length === 0
                        ? <option value="">{loadingInterfaces ? 'Cargando interfaces…' : 'Sin interfaces (usar manual)'}</option>
                        : interfaceOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)
                      }
                    </select>

                    <div className="d-flex gap-2 align-items-center">
                      <select className="form-select form-select-sm" value={assignmentType} onChange={e => setAssignmentType(e.target.value)} style={{ maxWidth: 120 }}>
                        <option value="static">Estática</option>
                        <option value="reserved">Reservada</option>
                      </select>
                      <div className="form-check mb-0">
                        <input className="form-check-input" type="checkbox" id="exPrimary"
                          checked={!!assignForm.is_primary}
                          disabled={assignForm.object_type === 'server_team' || assignmentType !== 'static'}
                          onChange={e => setAssignForm(p => ({ ...p, is_primary: e.target.checked }))} />
                        <label className="form-check-label small" htmlFor="exPrimary">Principal</label>
                      </div>
                    </div>

                    <input className="form-control form-control-sm" placeholder="Interfaz manual (ej. eth0)"
                      value={customInterfaceRef} onChange={e => setCustomInterfaceRef(e.target.value)} />
                  </>
                )}
                {assignForm.object_type === 'virtual_group' && assignForm.object_id && selectedIp && (
                  <div className="alert alert-info py-1 mb-0" style={{ fontSize: '0.82em' }}>
                    Asignará <strong className="font-monospace">{selectedIp}</strong> como IP virtual del grupo seleccionado.
                  </div>
                )}

                <button className="btn btn-success btn-sm" type="submit" disabled={!selectedIp || !assignForm.object_id}>
                  {selectedIp ? `Asignar ${selectedIp}` : 'Selecciona una IP'}
                </button>
              </form>
            </div>
          </div>

          {/* Active assignments (inline, compact) */}
          <div className="card mt-3">
            <div className="card-header py-2 d-flex justify-content-between align-items-center">
              <strong style={{ fontSize: '0.9em' }}>Asignadas en esta subred</strong>
              <span className="badge text-bg-secondary">
                {assignments.filter(a => a.subnet_id === Number(selectedSubnetId)).length}
              </span>
            </div>
            <div className="card-body p-0" style={{ maxHeight: 240, overflow: 'auto' }}>
              {(() => {
                const subnetAssignments = assignments.filter(a => a.subnet_id === Number(selectedSubnetId));
                if (subnetAssignments.length === 0) return <p className="text-muted small text-center py-2">Sin asignaciones en esta subred.</p>;
                return (
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                      <tr style={{ fontSize: '0.78em' }}>
                        <th>IP</th><th>Objeto</th><th>Modo</th><th></th>
                      </tr>
                    </thead>
                    <tbody style={{ fontSize: '0.78em' }}>
                      {subnetAssignments.map(a => (
                        <tr key={a.id}>
                          <td style={{ fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{a.ip}</td>
                          <td style={{ maxWidth: 140 }}>
                            <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                 title={assignmentName(a, objectNameByRef)}>
                              {assignmentName(a, objectNameByRef)}
                            </div>
                            <div className="text-muted" style={{ fontSize: '0.82em', lineHeight: 1.2 }}>
                              {ciTypeLabel(a.object_type)}
                              {a.interface_ref ? ` · ${a.interface_ref}` : ''}
                            </div>
                          </td>
                          <td>
                            <span className={`badge text-bg-${ASSIGN_TYPE_COLORS[a.assignment_type] || 'secondary'}`} style={{ fontSize: '0.7em' }}>
                              {(a.assignment_type || 'static').toUpperCase()}
                            </span>
                          </td>
                          <td>
                            <button className="btn btn-sm btn-outline-danger py-0 px-1" style={{ fontSize: '0.72em' }} onClick={() => release(a.id)}>
                              ✕
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </IPAMLayout>
  );
}
